import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useAuth0, User } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import api, { AuthAPI } from "../utils/axios";

/**
 * Auth context provides authentication state and methods to the application
 */
interface AuthContextType {
  user: User | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => Promise<void>;
  login: () => Promise<void>;
  token: string | undefined;
  error: string | null;
  validateUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const DASHBOARD_PATH = "/";

/**
 * AuthProvider uses the useAuth hook internally but exposes a context-based API
 * for components that prefer to use contexts over hooks
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    user,
    isAuthenticated,
    logout: auth0Logout,
    getAccessTokenSilently,
    loginWithRedirect,
    isLoading,
  } = useAuth0();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const login = useCallback(async (): Promise<void> => {
    await loginWithRedirect({
      appState: {
        returnTo: window.location.origin + DASHBOARD_PATH,
      },
      authorizationParams: {
        redirect_uri: import.meta.env.VITE_AUTH0_REDIRECT_URI,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: "openid profile email offline_access",
        grant_type: "authorization_code",
      },
    });
  }, [loginWithRedirect]);

  // Handle token fetching only - validation will be handled by React Query
  useEffect(() => {
    const fetchToken = async () => {
      if (!user || !isAuthenticated || token) return;

      try {
        const t = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: "openid profile email offline_access",
          },
        });
        setToken(t);
        api.defaults.headers.common["Authorization"] = `Bearer ${t}`;
      } catch (authError: any) {
        if (
          authError instanceof Error &&
          authError.message.includes("login_required")
        ) {
          login();
        }
      }
    };

    fetchToken();
  }, [user, isAuthenticated, token, getAccessTokenSilently, login]);

  const validateUser = useCallback(async () => {
    if (!user || isLoading || !isAuthenticated || !token) return;

    // Trigger React Query to fetch/validate - this will use the shared cache
    queryClient.invalidateQueries({ queryKey: ["auth", "validate"] });
  }, [user, isLoading, isAuthenticated, token, queryClient]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout: auth0Logout,
        isLoading,
        isAuthenticated,
        user,
        token,
        error,
        validateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook for consuming the auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
